import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, LogOut, Search, Inbox, Phone, Mail, AlertTriangle,
  RefreshCw, Users, Plus, Copy, Trash2, Check, ShieldCheck, UserPlus, Pencil, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";
import { STATUS_META, DRAFT_META, STATUS_OPTIONS } from "@/lib/formConfig";
import { LeadRecap } from "@/components/lead/LeadRecap";
import { LeadEditFields } from "@/components/lead/LeadEditFields";
import { toast } from "@/components/ui/sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const HTTP_UNAUTHORIZED = 401;
const SEARCH_DEBOUNCE_MS = 350;

const FILTERS = [
  { key: "tous", label: "Tous" },
  { key: "nouveau", label: "Nouveaux" },
  { key: "en_attente", label: "En attente" },
  { key: "rappele", label: "Rappelés" },
  { key: "en_cours", label: "En cours" },
  { key: "signe", label: "Signés" },
  { key: "perdu", label: "Perdus" },
  { key: "brouillon", label: "Non finalisés" },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const initials = (name) => {
  if (!name) return "PE";
  const words = name.replace(/\(.*?\)/g, "").trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] || "";
  const second = words[1]?.[0] || "";
  return (first + second).toUpperCase() || "PE";
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.nouveau;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${m.bg} ${m.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

function LeadBadge({ lead }) {
  if (lead.completion === "draft") {
    const m = DRAFT_META[lead.draft_state] || DRAFT_META.abandonne;
    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${m.bg} ${m.text}`} data-testid={`draft-badge-${lead.draft_state}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${m.dot} ${lead.draft_state === "en_cours" ? "animate-pulse" : ""}`} />
        {m.label}
      </span>
    );
  }
  return <StatusBadge status={lead.status} />;
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-1 font-heading text-3xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, nouveau: 0, en_attente: 0, rappele: 0, en_cours: 0, signe: 0, perdu: 0, brouillon: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tous");
  const [licenseeFilter, setLicenseeFilter] = useState("tous");
  const [licensees, setLicensees] = useState([]);
  const [licenseeSheetOpen, setLicenseeSheetOpen] = useState(false);
  const [accessSheetOpen, setAccessSheetOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/leads", { params: { status: filter, search: search || undefined, licensee: licenseeFilter } }),
        api.get("/leads/stats"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      if (e?.response?.status === HTTP_UNAUTHORIZED) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Impossible de charger les leads.");
    } finally {
      setLoading(false);
    }
  }, [filter, search, licenseeFilter, navigate]);

  useEffect(() => {
    const t = setTimeout(load, search ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const loadLicensees = useCallback(async () => {
    try {
      const res = await api.get("/licensees");
      setLicensees(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => { loadLicensees(); }, [loadLicensees]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get("gmail");
    if (g) {
      if (g === "connected") {
        toast.success("Gmail connecté : les emails de reprise seront envoyés depuis ce compte.");
      } else {
        const reason = params.get("reason");
        const messages = {
          access_denied: "Google a refusé l'accès. Ajoutez votre adresse Gmail dans « Test users » de l'écran de consentement OAuth (ou publiez l'application).",
          exchange: "Échec de l'échange avec Google. Vérifiez l'URI de redirection et le secret client dans Google Cloud Console.",
          state: "Session expirée. Relancez la connexion Gmail.",
        };
        toast.error(messages[reason] || "La connexion Gmail a échoué. Réessayez.");
      }
      params.delete("gmail");
      params.delete("reason");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const updateStatus = async (lead, status) => {
    try {
      const res = await api.patch(`/leads/${lead.id}/status`, { status });
      setSelected(res.data);
      load();
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Échec de la mise à jour");
    }
  };

  const saveLeadFields = async (lead, editData) => {
    try {
      const res = await api.patch(`/leads/${lead.id}`, { fields: editData });
      setSelected(res.data);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? res.data : l)));
      toast.success("Fiche mise à jour");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la mise à jour");
      return false;
    }
  };

  const assignLicensee = async (lead, licenseeId) => {
    try {
      const res = await api.patch(`/leads/${lead.id}/licensee`, { licensee_id: licenseeId || null });
      setSelected(res.data);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? res.data : l)));
      loadLicensees();
      toast.success("Apporteur mis à jour");
    } catch {
      toast.error("Échec de l'attribution");
    }
  };

  const createLicensee = async (name, email) => {
    const res = await api.post("/licensees", { name, email: email || null });
    setLicensees((prev) => [res.data, ...prev]);
    return res.data;
  };

  const deleteLicensee = async (id) => {
    await api.delete(`/licensees/${id}`);
    setLicensees((prev) => prev.filter((l) => l.id !== id));
    load();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Place des Énergies" className="h-12 w-auto" />
            <span className="hidden text-sm font-semibold text-slate-400 sm:inline">| Espace conseillère</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
              {initials(user?.name)}
            </span>
            <span className="hidden text-sm font-medium text-slate-700 sm:inline" data-testid="admin-user-name">{user?.name}</span>
            <button
              type="button"
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex h-9 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-7 sm:px-6">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground sm:text-3xl">Leads reçus</h1>
            <p className="mt-1 text-sm text-muted-foreground">Suivez et gérez les demandes des particuliers.</p>
          </div>
          <div className="flex items-center gap-2">
            {user?.is_owner && (
              <button
                type="button"
                data-testid="manage-access-button"
                onClick={() => setAccessSheetOpen(true)}
                className="flex h-10 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                <ShieldCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Accès</span>
              </button>
            )}
            <button
              type="button"
              data-testid="manage-licensees-button"
              onClick={() => setLicenseeSheetOpen(true)}
              className="flex h-10 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary/90"
            >
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Licenciés</span>
            </button>
            <button
              type="button"
              data-testid="refresh-button"
              onClick={load}
              className="flex h-10 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          <StatCard label="Total" value={stats.total} accent="text-foreground" />
          <StatCard label="Nouveaux" value={stats.nouveau} accent="text-blue-600" />
          <StatCard label="En attente" value={stats.en_attente} accent="text-orange-500" />
          <StatCard label="Rappelés" value={stats.rappele} accent="text-amber-500" />
          <StatCard label="En cours" value={stats.en_cours} accent="text-violet-600" />
          <StatCard label="Signés" value={stats.signe} accent="text-emerald-600" />
          <StatCard label="Perdus" value={stats.perdu} accent="text-rose-500" />
          <StatCard label="Non finalisés" value={stats.brouillon} accent="text-sky-600" />
        </div>

        {/* Filters + search */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={
                  "h-9 rounded-full px-4 text-sm font-semibold transition-colors " +
                  (filter === f.key ? "bg-primary text-white" : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50")
                }
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select value={licenseeFilter} onValueChange={setLicenseeFilter}>
              <SelectTrigger className="h-10 sm:w-48" data-testid="licensee-filter-trigger">
                <SelectValue placeholder="Apporteur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les apporteurs</SelectItem>
                {licensees.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-input"
                placeholder="Rechercher nom, ville, téléphone…"
                className="h-10 w-full rounded-full border border-slate-300 bg-white pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
              />
            </div>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="empty-state">
            <Inbox className="h-10 w-10 text-slate-300" />
            <p className="mt-3 font-semibold text-slate-600">Aucun lead pour le moment</p>
            <p className="text-sm text-muted-foreground">Les nouvelles demandes apparaîtront ici.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            {/* Desktop header */}
            <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
              <div className="col-span-3">Contact</div>
              <div className="col-span-3">Localisation</div>
              <div className="col-span-2">Logement</div>
              <div className="col-span-2">Reçu le</div>
              <div className="col-span-2 text-right">Statut</div>
            </div>
            <ul className="divide-y divide-slate-100">
              {leads.map((lead) => (
                <li key={lead.id}>
                  <button
                    type="button"
                    data-testid={`lead-row-${lead.id}`}
                    onClick={() => setSelected(lead)}
                    className="grid w-full grid-cols-1 gap-1 px-5 py-4 text-left transition-colors hover:bg-slate-50 sm:grid-cols-12 sm:items-center sm:gap-3"
                  >
                    <div className="col-span-3 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="truncate font-semibold text-foreground">{lead.prenom} {lead.nom}</p>
                        {lead.demande_urgente === "Oui" && (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">
                            <AlertTriangle className="h-3 w-3" /> Urgent
                          </span>
                        )}
                      </div>
                      <p className="truncate text-sm text-muted-foreground">{lead.telephone_portable}</p>
                      {lead.licensee_name && (
                        <p className="truncate text-xs font-medium text-primary" data-testid={`lead-licensee-${lead.id}`}>via {lead.licensee_name}</p>
                      )}
                    </div>
                    <div className="col-span-3 truncate text-sm text-slate-600">{lead.code_postal_ville || "—"}</div>
                    <div className="col-span-2 truncate text-sm text-slate-600">{lead.logement_type || "—"}</div>
                    <div className="col-span-2 text-sm text-slate-500">{fmtDate(lead.created_at)}</div>
                    <div className="col-span-2 mt-1 sm:mt-0 sm:text-right"><LeadBadge lead={lead} /></div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>

      <LeadSheet lead={selected} onClose={() => setSelected(null)} onStatusChange={updateStatus} licensees={licensees} onAssign={assignLicensee} onSaveFields={saveLeadFields} />
      <LicenseeSheet
        open={licenseeSheetOpen}
        onClose={() => setLicenseeSheetOpen(false)}
        licensees={licensees}
        onCreate={createLicensee}
        onDelete={deleteLicensee}
      />
      <AccessSheet
        open={accessSheetOpen}
        onClose={() => setAccessSheetOpen(false)}
        currentEmail={user?.email}
      />
    </div>
  );
}

function LeadSheet({ lead, onClose, onStatusChange, licensees, onAssign, onSaveFields }) {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setEditing(false); }, [lead?.id]);

  const startEdit = () => { setEditData({ ...lead }); setEditing(true); };
  const patch = (p) => setEditData((d) => ({ ...d, ...p }));
  const save = async () => {
    setSaving(true);
    const ok = await onSaveFields(lead, editData);
    setSaving(false);
    if (ok) setEditing(false);
  };

  const isDraft = lead?.completion === "draft";

  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="lead-detail-sheet">
        {lead && (
          <>
            <SheetHeader className="space-y-3 pb-2">
              <SheetTitle className="font-heading text-2xl">{lead.prenom} {lead.nom}</SheetTitle>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <LeadBadge lead={lead} />
                <Select
                  value={STATUS_OPTIONS.some((o) => o.value === lead.status) ? lead.status : undefined}
                  onValueChange={(v) => onStatusChange(lead, v)}
                >
                  <SelectTrigger className="h-9 w-44" data-testid="status-select-trigger">
                    <SelectValue placeholder={isDraft ? "Qualifier…" : "Statut"} />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`status-option-${o.value}`}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isDraft && (
                <p className="rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="draft-notice">
                  Non finalisé : le client a commencé la saisie sans l'envoyer. Choisir un statut (ex. « En attente ») le convertit en lead à traiter.
                </p>
              )}
              <div className="flex items-center gap-2">
                {!editing ? (
                  <button
                    type="button"
                    data-testid="edit-lead-button"
                    onClick={startEdit}
                    className="flex h-9 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    <Pencil className="h-4 w-4" /> Modifier la fiche
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      data-testid="save-lead-button"
                      onClick={save}
                      disabled={saving}
                      className="flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
                    </button>
                    <button
                      type="button"
                      data-testid="cancel-edit-button"
                      onClick={() => setEditing(false)}
                      className="flex h-9 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      <X className="h-4 w-4" /> Annuler
                    </button>
                  </>
                )}
              </div>
            </SheetHeader>

            <div className="space-y-1 py-3">
              {!editing && (
                <div className="mb-2 flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Apporteur</p>
                    <p className="truncate text-sm font-medium text-foreground" data-testid="lead-licensee-name">{lead.licensee_name || "Non attribué"}</p>
                  </div>
                  <Select value={lead.licensee_id || "none"} onValueChange={(v) => onAssign(lead, v === "none" ? null : v)}>
                    <SelectTrigger className="h-9 w-40" data-testid="assign-licensee-trigger">
                      <SelectValue placeholder="Attribuer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {(licensees || []).map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {editing ? (
                <LeadEditFields data={editData} set={patch} />
              ) : (
                <LeadRecap lead={lead} fileUrlFor={(p) => `/files/${p}`} />
              )}

              {!editing && (
                <div className="border-t border-slate-100 pt-4">
                  <a
                    href={`tel:${lead.telephone_portable}`}
                    data-testid="call-lead-button"
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                  >
                    <Phone className="h-4 w-4" /> Appeler {lead.prenom}
                  </a>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}


function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copie impossible");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={`copy-${label.toLowerCase()}`}
      className="flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copié" : label}
    </button>
  );
}

function LicenseeSheet({ open, onClose, licensees, onCreate, onDelete }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const origin = window.location.origin;

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      await onCreate(name.trim(), email.trim());
      setName("");
      setEmail("");
      toast.success("Licencié créé");
    } catch {
      toast.error("Échec de la création");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="licensee-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl">Licenciés / apporteurs</SheetTitle>
        </SheetHeader>
        <p className="mt-1 text-sm text-muted-foreground">
          Créez un licencié, puis transmettez-lui ses 2 liens : le lien de <b>partage</b> (pour ses clients) et le lien de <b>suivi</b> (privé, à ne pas diffuser).
        </p>

        <div className="mt-5 space-y-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nom du licencié"
            data-testid="new-licensee-input"
            className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            type="email"
            placeholder="Email du licencié (optionnel)"
            data-testid="new-licensee-email"
            className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            data-testid="create-licensee-button"
            className="flex h-11 w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            <Plus className="h-4 w-4" /> Ajouter le licencié
          </button>
        </div>

        <div className="mt-6 space-y-3 pb-8">
          {licensees.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Aucun licencié pour le moment.</p>
          ) : (
            licensees.map((l) => {
              const shareLink = `${origin}/?ref=${l.ref_code}`;
              const trackLink = `${origin}/suivi/${l.tracking_token}`;
              const mailSubject = encodeURIComponent("Votre lien de suivi des leads — Place des Énergies");
              const mailBody = encodeURIComponent(
                `Bonjour ${l.name},\n\n` +
                `Voici votre lien personnel pour suivre en temps réel les leads que vous nous transmettez et leur avancement :\n${trackLink}\n` +
                `(Ce lien est personnel, merci de ne pas le diffuser.)\n\n` +
                `Et voici le lien du formulaire à partager à vos clients :\n${shareLink}\n\n` +
                `Bien à vous,\nPlace des Énergies`
              );
              const mailHref = `mailto:${l.email || ""}?subject=${mailSubject}&body=${mailBody}`;
              return (
                <div key={l.id} className="rounded-xl border border-slate-200 p-4" data-testid={`licensee-card-${l.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground">{l.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {l.lead_count ?? 0} lead(s){l.email ? ` · ${l.email}` : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onDelete(l.id)}
                      data-testid={`delete-licensee-${l.id}`}
                      className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Lien de partage (clients)</p>
                        <p className="truncate text-xs text-slate-600">{shareLink}</p>
                      </div>
                      <CopyButton value={shareLink} label="Partage" />
                    </div>
                    <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Lien de suivi (privé)</p>
                        <p className="truncate text-xs text-slate-600">{trackLink}</p>
                      </div>
                      <CopyButton value={trackLink} label="Suivi" />
                    </div>
                  </div>
                  <a
                    href={mailHref}
                    data-testid={`email-licensee-${l.id}`}
                    className="mt-2 flex h-9 items-center justify-center gap-1.5 rounded-lg border border-primary/30 bg-primary/[0.04] text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
                  >
                    <Mail className="h-3.5 w-3.5" /> Envoyer les liens par email
                  </a>
                </div>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AccessSheet({ open, onClose, currentEmail }) {
  const [owners, setOwners] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [gmail, setGmail] = useState({ connected: false, email: null, configured: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [adminsRes, gmailRes] = await Promise.all([
        api.get("/admins"),
        api.get("/gmail/status"),
      ]);
      setOwners(adminsRes.data.owners || []);
      setAllowed((adminsRes.data.allowed || []).filter((e) => !(adminsRes.data.owners || []).includes(e)));
      setGmail(gmailRes.data);
    } catch {
      toast.error("Impossible de charger les accès");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const connectGmail = async () => {
    try {
      const res = await api.get("/oauth/gmail/login", { params: { origin: window.location.origin } });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer la connexion Gmail.");
    }
  };

  const disconnectGmail = async () => {
    try {
      await api.post("/gmail/disconnect");
      setGmail((g) => ({ ...g, connected: false, email: null }));
      toast.success("Gmail déconnecté");
    } catch {
      toast.error("Échec de la déconnexion");
    }
  };

  const handleAdd = async () => {
    const value = email.trim().toLowerCase();
    if (!value) return;
    setAdding(true);
    try {
      await api.post("/admins", { email: value });
      setEmail("");
      await load();
      toast.success("Accès accordé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'ajout");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (value) => {
    try {
      await api.delete(`/admins/${encodeURIComponent(value)}`);
      setAllowed((prev) => prev.filter((e) => e !== value));
      toast.success("Accès retiré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du retrait");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="access-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl">Accès / Utilisateurs</SheetTitle>
        </SheetHeader>
        <p className="mt-1 text-sm text-muted-foreground">
          Gérez les comptes Google autorisés à se connecter à l'espace conseillère. Seuls les emails listés ici pourront accéder au tableau de bord.
        </p>

        <div className="mt-5 flex gap-2">
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            type="email"
            placeholder="email@placedesenergies.com"
            data-testid="new-admin-email"
            className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={adding}
            data-testid="add-admin-button"
            className="flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-xl bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Inviter
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="mt-6 space-y-6 pb-8">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Propriétaires</p>
              <div className="space-y-2">
                {owners.map((e) => (
                  <div key={e} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`owner-row-${e}`}>
                    <div className="flex min-w-0 items-center gap-2">
                      <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
                      <span className="truncate text-sm font-medium text-foreground">{e}</span>
                    </div>
                    <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-bold uppercase text-primary">Propriétaire</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Conseillères autorisées</p>
              {allowed.length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-300 py-8 text-center text-sm text-muted-foreground" data-testid="access-empty-state">
                  Aucune conseillère ajoutée pour le moment.
                </p>
              ) : (
                <div className="space-y-2">
                  {allowed.map((e) => (
                    <div key={e} className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3" data-testid={`admin-row-${e}`}>
                      <span className="truncate text-sm font-medium text-foreground">{e}</span>
                      {e === (currentEmail || "").toLowerCase() ? (
                        <span className="shrink-0 text-xs font-medium text-muted-foreground">Vous</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRemove(e)}
                          data-testid={`remove-admin-${e}`}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Envoi automatique des emails</p>
              <div className="rounded-xl border border-slate-200 p-4" data-testid="gmail-settings">
                <div className="flex items-start gap-2">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">Compte Gmail expéditeur</p>
                    {gmail.connected ? (
                      <p className="truncate text-xs font-medium text-emerald-600" data-testid="gmail-connected-email">Connecté : {gmail.email}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Connectez un compte Gmail pour envoyer automatiquement aux prospects leur lien de reprise (sinon leur application mail s'ouvre).
                      </p>
                    )}
                  </div>
                </div>
                {!gmail.connected && gmail.last_error && (
                  <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-[11px] leading-relaxed text-rose-700" data-testid="gmail-last-error">
                    Dernière erreur : {gmail.last_error}
                  </p>
                )}
                <div className="mt-3">
                  {gmail.connected ? (
                    <button
                      type="button"
                      onClick={disconnectGmail}
                      data-testid="gmail-disconnect-button"
                      className="flex h-10 w-full items-center justify-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                    >
                      Déconnecter
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={connectGmail}
                      data-testid="gmail-connect-button"
                      className="flex h-10 w-full items-center justify-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                    >
                      <Mail className="h-4 w-4" /> Connecter Gmail
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
